// Booking workflow status per unit, lifted from the old Office Home page (read-only here).
import { BUC_DEP_KEY, FULL_PAYMENT_DEP_KEY, PRE_BUC_DEP_KEY, PRE_DEED_STEP_KEYS } from '../deedPossessionGates';
import { yesAnswerForStep, yesAnswerStepKey } from '../workflowAnswerGate';

export const UNIT_BOOKED_DEP_KEY = '__unit_booked__';
export const LOAN_REQUIRED_DEP_KEY = '__loan_required_yes__';

export type WorkflowDefinitionStep = {
  key: string;
  title: string;
  order: number;
  enabled?: boolean;
  showInDashboard?: boolean;
  keepActive?: boolean;
  /** Lifecycle stage picked in the workflow editor (empty = worked out from the key and title). */
  stage?: string;
  dependsOnSteps?: string[];
  dependsMode?: 'all' | 'any';
  actionType?: string;
  fieldKey?: string;
  docKey?: string;
  paymentType?: string;
  helpText?: string;
};

export type WorkflowCase = {
  caseType?: string;
  status?: string;
  loanRequired?: 'yes' | 'no' | null;
  completedStepKeys?: string[];
  stepData?: Record<string, any>;
  stepState?: Record<string, any>;
  startedAt?: any;
  preferredBankName?: string | null;
};

export type DrawerActionType = 'add_customer' | 'add_kyc' | 'upload_booking_form' | 'booking_form' | 'download_receipt' | 'start_loan' | 'stop_loan' | 'regenerate_doc' | 'add_dp' | 'add_gst' | 'loan_sanction_form' | 'edit_sanction' | 'auto_booking_form' | 'add_advocate_fee';

export type PendingStep = {
  unitId: string;
  workflowType: 'main' | 'loan';
  status: 'ready' | 'blocked' | 'not_started';
  stepKey: string;
  stepTitle: string;
  order: number;
  action: { label: string; type: DrawerActionType } | null;
};

export function normalizeDefinitionSteps(input: any[]): WorkflowDefinitionStep[] {
  const raw = Array.isArray(input) ? input : [];
  return raw
    .map((s: any, idx: number): WorkflowDefinitionStep => ({
      key: String(s?.key || '').trim(),
      title: String(s?.title || s?.key || `Step ${idx + 1}`).trim(),
      order: Number(s?.order || idx + 1),
      enabled: s?.enabled !== false,
      showInDashboard: s?.showInDashboard !== false,
      keepActive: !!s?.keepActive,
      dependsOnSteps: (Array.isArray(s?.dependsOnSteps) ? s.dependsOnSteps : []).map((v: any) => String(v || '').trim()).filter(Boolean),
      dependsMode: String(s?.dependsMode || 'all') === 'any' ? 'any' : 'all',
      actionType: String(s?.actionType || 'mark_done').trim(),
      fieldKey: String(s?.fieldKey || '').trim(),
      docKey: String(s?.docKey || '').trim(),
      paymentType: String(s?.paymentType || '').trim(),
      helpText: String(s?.helpText || '').trim(),
      stage: String(s?.stage || '').trim(),
    }))
    .filter((s) => s.key)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

/** Step keys done on a case: listed in completedStepKeys or marked done in stepState. */
export function completedKeysOf(c: WorkflowCase | null | undefined): string[] {
  if (!c) return [];
  const state = c.stepState || {};
  const fromState = Object.keys(state).filter((k) => state[k]?.done === true || !!state[k]?.doneAt || !!state[k]?.doneAtMs);
  return Array.from(new Set([...(c.completedStepKeys || []), ...fromState].map((k) => String(k || '').trim()).filter(Boolean)));
}

function computeSteps(steps: WorkflowDefinitionStep[], workflowCase: WorkflowCase | null | undefined, completed: Set<string>, otherCompleted: Set<string>, loanRequiredYes: boolean, gates: { fullPayment: boolean; bucReceived: boolean; registeredDeed: boolean }) {
  return steps
    .filter((s) => s.enabled !== false)
    .map((step) => {
      if (gates.registeredDeed && PRE_DEED_STEP_KEYS.has(step.key)) return { step, status: 'done' as const };
      if (completed.has(step.key)) return { step, status: step.keepActive ? ('ready' as const) : ('done' as const) };
      const deps = step.dependsOnSteps || [];
      if (deps.some((dep) => yesAnswerStepKey(dep) && !yesAnswerForStep(dep, steps, workflowCase))) return { step, status: 'not_applicable' as const };
      if (!deps.length) return { step, status: 'ready' as const };
      const isDepDone = (dep: string) => {
        if (yesAnswerStepKey(dep)) return yesAnswerForStep(dep, steps, workflowCase);
        if (dep === UNIT_BOOKED_DEP_KEY) return true;
        if (dep === LOAN_REQUIRED_DEP_KEY) return loanRequiredYes;
        if (dep === FULL_PAYMENT_DEP_KEY) return gates.fullPayment;
        if (dep === BUC_DEP_KEY) return gates.bucReceived;
        if (dep === PRE_BUC_DEP_KEY) return !gates.bucReceived;
        if (gates.registeredDeed && PRE_DEED_STEP_KEYS.has(dep)) return true;
        if (dep.startsWith('main:') || dep.startsWith('loan:')) return otherCompleted.has(dep.split(':').slice(1).join(':').trim());
        return completed.has(dep);
      };
      const blocked = deps.filter((d) => !isDepDone(d));
      const ready = step.dependsMode === 'any' ? blocked.length < deps.length : blocked.length === 0;
      return { step, status: ready ? ('ready' as const) : ('blocked' as const) };
    });
}

export function actionForStep(step: WorkflowDefinitionStep): { label: string; type: DrawerActionType } | null {
  const actionType = String(step.actionType || '').toLowerCase();
  if (actionType === 'add_customer') return { label: 'Add customer', type: 'add_customer' };
  if (actionType === 'add_kyc') return { label: 'Add KYC', type: 'add_kyc' };
  if (actionType === 'upload_document' && String(step.docKey || '').toLowerCase() === 'booking_form_signed') return { label: 'Upload', type: 'upload_booking_form' };
  if (actionType === 'add_payment') return String(step.paymentType || '').toLowerCase() === 'gst' ? { label: 'Add GST', type: 'add_gst' } : { label: 'Add DP', type: 'add_dp' };
  if (actionType === 'advocate_agreement_fee_receipt') return { label: 'Add receipt', type: 'add_advocate_fee' };
  if (actionType === 'loan_sanction_form') return { label: 'Open form', type: 'loan_sanction_form' };
  return null;
}

/** Latest sanctioned bank on a loan case, used for step titles like "Disbursal from {{loan_sanction_bank_name}}". */
export function sanctionOf(loanCase: WorkflowCase | null | undefined, loanSteps: WorkflowDefinitionStep[]) {
  const data = loanCase?.stepData || {};
  const done = new Set(completedKeysOf(loanCase));
  const hits = loanSteps
    .filter((s) => String(s.actionType || '').toLowerCase() === 'loan_sanction_form')
    .map((s) => ({ bank: String(data[s.key]?.sanctionBankName || '').trim(), date: String(data[s.key]?.sanctionDate || '').trim(), done: done.has(s.key) }))
    .filter((h) => h.bank || h.done)
    .sort((a, b) => b.date.localeCompare(a.date));
  return { sanctioned: hits.some((h) => h.done), bank: hits.find((h) => h.bank)?.bank || '' };
}

const titleWith = (title: string, bank: string) =>
  title.replace(/\{\{\s*loan_sanction_bank_name\s*\}\}/gi, bank || 'Bank').replace(/\s{2,}/g, ' ').trim();

/** Every open step for one booked unit, main workflow first, then loan when a loan is needed. */
export function pendingStepsForUnit(args: {
  unitId: string;
  mainCase: WorkflowCase | null | undefined;
  loanCase: WorkflowCase | null | undefined;
  mainSteps: WorkflowDefinitionStep[];
  loanSteps: WorkflowDefinitionStep[];
  fullPayment?: boolean;
  bucReceived?: boolean;
  registeredDeed?: boolean;
}): PendingStep[] {
  const { unitId, mainCase, loanCase, mainSteps, loanSteps } = args;
  if (!mainCase) return [{ unitId, workflowType: 'main', status: 'not_started', stepKey: 'workflow_not_started', stepTitle: 'Workflow not started', order: 0, action: null }];
  const loanRequiredYes = String(mainCase.loanRequired || '').toLowerCase() === 'yes';
  const mainDone = new Set(completedKeysOf(mainCase));
  const loanDone = new Set(completedKeysOf(loanCase));
  const bank = sanctionOf(loanCase, loanSteps).bank;
  const out: PendingStep[] = [];
  const add = (type: 'main' | 'loan', steps: WorkflowDefinitionStep[], done: Set<string>, other: Set<string>) => {
    computeSteps(steps, type === 'main' ? mainCase : loanCase, done, other, loanRequiredYes, { fullPayment: !!args.fullPayment, bucReceived: !!args.bucReceived, registeredDeed: !!args.registeredDeed }).forEach(({ step, status }) => {
      if (status === 'done' || status === 'not_applicable' || step.showInDashboard === false) return;
      out.push({ unitId, workflowType: type, status, stepKey: step.key, stepTitle: titleWith(step.title, bank), order: step.order + (type === 'loan' ? 1000 : 0), action: status === 'ready' ? actionForStep(step) : null });
    });
  };
  add('main', mainSteps, mainDone, loanDone);
  if (loanRequiredYes && loanCase) add('loan', loanSteps, loanDone, mainDone);
  return out;
}

export type UnitStep = { workflowType: 'main' | 'loan'; key: string; title: string; status: 'done' | 'ready' | 'blocked'; order: number; action: { label: string; type: DrawerActionType } | null; stage?: string };

/** Every step for one booked unit with its status, for the unit page's step bar. Null when no workflow was started. */
export function allStepsForUnit(args: {
  mainCase: WorkflowCase | null | undefined;
  loanCase: WorkflowCase | null | undefined;
  mainSteps: WorkflowDefinitionStep[];
  loanSteps: WorkflowDefinitionStep[];
  fullPayment?: boolean;
  bucReceived?: boolean;
  registeredDeed?: boolean;
}): UnitStep[] | null {
  const { mainCase, loanCase, mainSteps, loanSteps } = args;
  if (!mainCase) return null;
  const loanRequiredYes = String(mainCase.loanRequired || '').toLowerCase() === 'yes';
  const mainDone = new Set(completedKeysOf(mainCase));
  const loanDone = new Set(completedKeysOf(loanCase));
  const bank = sanctionOf(loanCase, loanSteps).bank;
  const gates = { fullPayment: !!args.fullPayment, bucReceived: !!args.bucReceived, registeredDeed: !!args.registeredDeed };
  const out: UnitStep[] = [];
  const add = (type: 'main' | 'loan', steps: WorkflowDefinitionStep[], done: Set<string>, other: Set<string>) => {
    computeSteps(steps, type === 'main' ? mainCase : loanCase, done, other, loanRequiredYes, gates).forEach(({ step, status }) => {
      if (status === 'not_applicable' || step.showInDashboard === false) return;
      out.push({ workflowType: type, key: step.key, title: titleWith(step.title, bank), status, order: step.order, action: status === 'ready' ? actionForStep(step) : null, stage: step.stage || '' });
    });
  };
  add('main', mainSteps, mainDone, loanDone);
  if (loanRequiredYes && loanCase) add('loan', loanSteps, loanDone, mainDone);
  return out;
}
